import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Button,
  Alert,
  Chip,
  alpha,
  useTheme,
  IconButton,
  Menu,
  ListItemText,
  List,
  ListItem,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Star as StarIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import {
  DayOfWeek,
  OccasionType,
  InheritanceMode,
  DayScheduleV2,
  UnifiedHoursState,
  SpecialHoursEntryV2,
  DAYS_ORDER,
  OCCASION_LABELS,
  OCCASION_ICONS,
  OCCASION_HELP
} from './types';
import { DayHoursRow } from './DayHoursRow';
import { HelpTooltip } from '../Onboarding/HelpTooltip';
import { AddSpecialHoursDialog, SpecialHoursEntry } from './AddSpecialHoursDialog';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { formatTime12Hr, formatTime24Hr, formatDayRange } from './utils';
import { format, parseISO } from 'date-fns';

interface UnifiedHoursEditorProps {
  // All occasions that can be edited
  availableOccasions: OccasionType[];
  // Initial hours data for each occasion (if any)
  initialHours?: Record<OccasionType, {
    enabled: boolean;
    schedule: Record<DayOfWeek, DayScheduleV2>;
  }>;
  // Initial special hours for each occasion (if any)
  initialSpecialHours?: Record<OccasionType, SpecialHoursEntryV2[]>;
  // Callback when hours change
  onChange: (state: UnifiedHoursState) => void;
  // Whether to show templates
  showTemplates?: boolean;
  // Whether to show special hours section
  showSpecialHours?: boolean;
}

const TEMPLATES = [
  {
    id: 'standard',
    name: 'Standard Restaurant',
    description: 'Mon-Thu 11-9, Fri-Sat 11-10, Sun 12-8',
    apply: (): Record<DayOfWeek, DayScheduleV2> => ({
      MONDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '21:00' }] },
      TUESDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '21:00' }] },
      WEDNESDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '21:00' }] },
      THURSDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '21:00' }] },
      FRIDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '22:00' }] },
      SATURDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '22:00' }] },
      SUNDAY: { enabled: true, timeSlots: [{ open: '12:00', close: '20:00' }] }
    })
  },
  {
    id: 'fast-food',
    name: 'Fast Food',
    description: 'Daily 6am-12am',
    apply: (): Record<DayOfWeek, DayScheduleV2> => {
      const schedule: Record<DayOfWeek, DayScheduleV2> = {} as any;
      DAYS_ORDER.forEach(day => {
        schedule[day] = { enabled: true, timeSlots: [{ open: '06:00', close: '00:00' }] };
      });
      return schedule;
    }
  },
  {
    id: '24-7',
    name: '24/7 Operation',
    description: 'Always open',
    apply: (): Record<DayOfWeek, DayScheduleV2> => {
      const schedule: Record<DayOfWeek, DayScheduleV2> = {} as any;
      DAYS_ORDER.forEach(day => {
        schedule[day] = { enabled: true, timeSlots: [{ open: '00:00', close: '23:59' }] };
      });
      return schedule;
    }
  },
  {
    id: 'lunch-dinner',
    name: 'Lunch & Dinner',
    description: 'Mon-Sat 11-2 & 5-10, Sun closed',
    apply: (): Record<DayOfWeek, DayScheduleV2> => ({
      MONDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '14:00' }, { open: '17:00', close: '22:00' }] },
      TUESDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '14:00' }, { open: '17:00', close: '22:00' }] },
      WEDNESDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '14:00' }, { open: '17:00', close: '22:00' }] },
      THURSDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '14:00' }, { open: '17:00', close: '22:00' }] },
      FRIDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '14:00' }, { open: '17:00', close: '22:00' }] },
      SATURDAY: { enabled: true, timeSlots: [{ open: '11:00', close: '14:00' }, { open: '17:00', close: '22:00' }] },
      SUNDAY: { enabled: false, timeSlots: [] }
    })
  }
];

// Helper to create default schedule
const createDefaultSchedule = (): Record<DayOfWeek, DayScheduleV2> => {
  const schedule: Record<DayOfWeek, DayScheduleV2> = {} as any;
  DAYS_ORDER.forEach(day => {
    schedule[day] = {
      enabled: day !== 'SUNDAY',
      timeSlots: [{ open: '10:30', close: '22:45' }]
    };
  });
  return schedule;
};

// Helper to summarize hours for display
const summarizeHours = (schedule: Record<DayOfWeek, DayScheduleV2>, use24Hour: boolean): string => {
  const formatTime = use24Hour ? formatTime24Hr : formatTime12Hr;
  const enabledDays = DAYS_ORDER.filter(day => schedule[day]?.enabled);

  if (enabledDays.length === 0) return 'Closed';
  if (enabledDays.length === 7) {
    const firstSlot = schedule['MONDAY']?.timeSlots?.[0];
    if (firstSlot) {
      return `Daily ${formatTime(firstSlot.open)} - ${formatTime(firstSlot.close)}`;
    }
  }

  // Check if all enabled days have same hours
  const firstDaySlots = JSON.stringify(schedule[enabledDays[0]]?.timeSlots);
  const allSame = enabledDays.every(day =>
    JSON.stringify(schedule[day]?.timeSlots) === firstDaySlots
  );

  if (allSame && schedule[enabledDays[0]]?.timeSlots?.[0]) {
    const slot = schedule[enabledDays[0]].timeSlots[0];
    const dayRange = formatDayRange(enabledDays);
    return `${dayRange}: ${formatTime(slot.open)} - ${formatTime(slot.close)}`;
  }

  return `${enabledDays.length} days configured`;
};

export const UnifiedHoursEditor: React.FC<UnifiedHoursEditorProps> = ({
  availableOccasions,
  initialHours,
  initialSpecialHours,
  onChange,
  showTemplates = true,
  showSpecialHours = true
}) => {
  const theme = useTheme();
  const { use24HourFormat } = useTimeFormat();

  // State
  // Carryout is the primary when the store has it; otherwise the first available occasion.
  const defaultPrimaryOccasion: OccasionType = availableOccasions.includes('CARRYOUT')
    ? 'CARRYOUT'
    : availableOccasions[0];
  const [primaryOccasion, setPrimaryOccasion] = useState<OccasionType>(defaultPrimaryOccasion);
  const [primarySchedule, setPrimarySchedule] = useState<Record<DayOfWeek, DayScheduleV2>>(createDefaultSchedule);
  const [occasionModes, setOccasionModes] = useState<Record<OccasionType, InheritanceMode>>(() => {
    const modes: Record<OccasionType, InheritanceMode> = {} as any;
    availableOccasions.forEach(occ => {
      modes[occ] = occ === defaultPrimaryOccasion ? 'override' : 'inherit';
    });
    return modes;
  });
  const [overrideSchedules, setOverrideSchedules] = useState<Record<OccasionType, Record<DayOfWeek, DayScheduleV2>>>({} as any);
  const [expandedOccasions, setExpandedOccasions] = useState<Set<OccasionType>>(new Set());

  // Special hours state
  const [specialHours, setSpecialHours] = useState<Record<OccasionType, SpecialHoursEntryV2[]>>(() => {
    if (initialSpecialHours) {
      return { ...initialSpecialHours };
    }
    const initial: Record<OccasionType, SpecialHoursEntryV2[]> = {} as any;
    availableOccasions.forEach(occ => {
      initial[occ] = [];
    });
    return initial;
  });
  const [specialHoursDialogOpen, setSpecialHoursDialogOpen] = useState(false);
  const [addingSpecialHoursFor, setAddingSpecialHoursFor] = useState<OccasionType | 'all' | null>(null);
  const [specialHoursMode, setSpecialHoursMode] = useState<'inherit' | 'override'>('inherit');
  const [editingSpecialHoursEntry, setEditingSpecialHoursEntry] = useState<SpecialHoursEntryV2 | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    occasion: OccasionType | null;
    entry: SpecialHoursEntryV2 | null;
  }>({ open: false, occasion: null, entry: null });

  // Get today's day
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase() as DayOfWeek;

  // Helper to check if a schedule has any enabled days with actual hours
  const hasActualHours = (schedule: Record<DayOfWeek, DayScheduleV2> | undefined): boolean => {
    if (!schedule) return false;
    return DAYS_ORDER.some(day =>
      schedule[day]?.enabled &&
      schedule[day]?.timeSlots?.length > 0 &&
      schedule[day].timeSlots.some(slot => slot.open && slot.close)
    );
  };

  // Initialize from initial hours if provided
  useEffect(() => {
    if (initialHours) {
      // Find a good primary occasion (one with actual hours configured)
      const occasionWithHours = availableOccasions.find(occ =>
        initialHours[occ]?.enabled && hasActualHours(initialHours[occ]?.schedule)
      ) || availableOccasions[0];

      if (occasionWithHours && initialHours[occasionWithHours]) {
        setPrimaryOccasion(occasionWithHours);
        setPrimarySchedule(initialHours[occasionWithHours].schedule);

        // Set all others to inherit by default, unless they have MEANINGFUL different hours
        const newModes: Record<OccasionType, InheritanceMode> = {} as any;
        const newOverrides: Record<OccasionType, Record<DayOfWeek, DayScheduleV2>> = {} as any;

        availableOccasions.forEach(occ => {
          if (occ === occasionWithHours) {
            newModes[occ] = 'override'; // Primary is always "override" internally
          } else if (initialHours[occ]?.schedule && hasActualHours(initialHours[occ]?.schedule)) {
            // Only set to override if this occasion has actual hours configured
            // and those hours differ from primary
            const primaryHours = JSON.stringify(initialHours[occasionWithHours].schedule);
            const thisHours = JSON.stringify(initialHours[occ].schedule);
            if (primaryHours !== thisHours) {
              newModes[occ] = 'override';
              newOverrides[occ] = initialHours[occ].schedule;
            } else {
              newModes[occ] = 'inherit';
            }
          } else {
            // No hours or all closed - default to inherit
            newModes[occ] = 'inherit';
          }
        });

        setOccasionModes(newModes);
        setOverrideSchedules(newOverrides);
      }
    }
  }, []);

  // Notify parent of changes
  useEffect(() => {
    const state: UnifiedHoursState = {
      primaryOccasion,
      primarySchedule,
      occasionSettings: {} as any,
      specialHours: specialHours
    };

    availableOccasions.forEach(occ => {
      state.occasionSettings[occ] = {
        occasion: occ,
        mode: occ === primaryOccasion ? 'override' : occasionModes[occ],
        customSchedule: occ === primaryOccasion
          ? primarySchedule
          : occasionModes[occ] === 'override'
            ? overrideSchedules[occ] || primarySchedule
            : undefined
      };
    });

    onChange(state);
  }, [primaryOccasion, primarySchedule, occasionModes, overrideSchedules, availableOccasions, specialHours]);

  // Handlers for primary schedule
  const handlePrimaryToggleDay = (day: DayOfWeek, enabled: boolean) => {
    setPrimarySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled,
        timeSlots: enabled && (!prev[day]?.timeSlots || prev[day].timeSlots.length === 0)
          ? [{ open: '10:30', close: '22:45' }]
          : prev[day]?.timeSlots || []
      }
    }));
  };

  const handlePrimaryUpdateTimeSlot = (day: DayOfWeek, index: number, field: 'open' | 'close', value: string) => {
    setPrimarySchedule(prev => {
      const newSlots = [...(prev[day]?.timeSlots || [])];
      newSlots[index] = { ...newSlots[index], [field]: value };
      return {
        ...prev,
        [day]: { ...prev[day], timeSlots: newSlots }
      };
    });
  };

  const handlePrimaryAddTimeSlot = (day: DayOfWeek) => {
    setPrimarySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        timeSlots: [...(prev[day]?.timeSlots || []), { open: '17:00', close: '22:00' }]
      }
    }));
  };

  const handlePrimaryRemoveTimeSlot = (day: DayOfWeek, index: number) => {
    setPrimarySchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        timeSlots: prev[day].timeSlots.filter((_, i) => i !== index)
      }
    }));
  };

  // Handlers for override schedules
  const handleOverrideToggleDay = (occasion: OccasionType, day: DayOfWeek, enabled: boolean) => {
    setOverrideSchedules(prev => {
      const schedule = prev[occasion] || { ...primarySchedule };
      return {
        ...prev,
        [occasion]: {
          ...schedule,
          [day]: {
            ...schedule[day],
            enabled,
            timeSlots: enabled && (!schedule[day]?.timeSlots || schedule[day].timeSlots.length === 0)
              ? [{ open: '10:30', close: '22:45' }]
              : schedule[day]?.timeSlots || []
          }
        }
      };
    });
  };

  const handleOverrideUpdateTimeSlot = (occasion: OccasionType, day: DayOfWeek, index: number, field: 'open' | 'close', value: string) => {
    setOverrideSchedules(prev => {
      const schedule = prev[occasion] || { ...primarySchedule };
      const newSlots = [...(schedule[day]?.timeSlots || [])];
      newSlots[index] = { ...newSlots[index], [field]: value };
      return {
        ...prev,
        [occasion]: {
          ...schedule,
          [day]: { ...schedule[day], timeSlots: newSlots }
        }
      };
    });
  };

  const handleOverrideAddTimeSlot = (occasion: OccasionType, day: DayOfWeek) => {
    setOverrideSchedules(prev => {
      const schedule = prev[occasion] || { ...primarySchedule };
      return {
        ...prev,
        [occasion]: {
          ...schedule,
          [day]: {
            ...schedule[day],
            timeSlots: [...(schedule[day]?.timeSlots || []), { open: '17:00', close: '22:00' }]
          }
        }
      };
    });
  };

  const handleOverrideRemoveTimeSlot = (occasion: OccasionType, day: DayOfWeek, index: number) => {
    setOverrideSchedules(prev => {
      const schedule = prev[occasion] || { ...primarySchedule };
      return {
        ...prev,
        [occasion]: {
          ...schedule,
          [day]: {
            ...schedule[day],
            timeSlots: schedule[day].timeSlots.filter((_, i) => i !== index)
          }
        }
      };
    });
  };

  // Mode change handler
  const handleModeChange = (occasion: OccasionType, mode: InheritanceMode) => {
    setOccasionModes(prev => ({ ...prev, [occasion]: mode }));

    // When switching to override, ALWAYS copy from primary as starting point
    // This gives users a good baseline to modify rather than starting from scratch
    if (mode === 'override') {
      setOverrideSchedules(prev => ({
        ...prev,
        [occasion]: JSON.parse(JSON.stringify(primarySchedule))
      }));
      // Auto-expand when switching to override
      setExpandedOccasions(prev => new Set([...prev, occasion]));
    }
  };

  // Primary occasion change handler
  const handlePrimaryChange = (newPrimary: OccasionType) => {
    // Save current primary schedule as override for old primary
    const oldPrimary = primaryOccasion;

    // Get the schedule for the new primary
    const newPrimarySchedule = occasionModes[newPrimary] === 'override' && overrideSchedules[newPrimary]
      ? overrideSchedules[newPrimary]
      : primarySchedule;

    setPrimaryOccasion(newPrimary);
    setPrimarySchedule(newPrimarySchedule);

    // Update modes - old primary becomes inherit, new primary is special
    setOccasionModes(prev => ({
      ...prev,
      [oldPrimary]: 'inherit',
      [newPrimary]: 'override'
    }));
  };

  // Apply template
  const applyTemplate = (templateId: string) => {
    const template = TEMPLATES.find(t => t.id === templateId);
    if (!template) return;
    setPrimarySchedule(template.apply());
  };

  // Toggle expansion
  const toggleExpanded = (occasion: OccasionType) => {
    setExpandedOccasions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(occasion)) {
        newSet.delete(occasion);
      } else {
        newSet.add(occasion);
      }
      return newSet;
    });
  };

  // Special hours handlers
  const handleAddSpecialHours = (forOccasion: OccasionType | 'all') => {
    setAddingSpecialHoursFor(forOccasion);
    setSpecialHoursDialogOpen(true);
  };

  const handleSaveSpecialHours = (entry: SpecialHoursEntry) => {
    const isEditing = editingSpecialHoursEntry !== null;

    const savedEntry: SpecialHoursEntryV2 = {
      id: entry.id || editingSpecialHoursEntry?.id || `special-${Date.now()}`,
      startDate: format(entry.startDate, 'yyyy-MM-dd'),
      endDate: format(entry.endDate, 'yyyy-MM-dd'),
      isClosed: entry.isClosed,
      openTime: entry.openTime,
      closeTime: entry.closeTime
    };

    if (isEditing) {
      // Update existing entry
      setSpecialHours(prev => {
        const updated = { ...prev };
        availableOccasions.forEach(occ => {
          updated[occ] = (updated[occ] || []).map(e =>
            e.id === savedEntry.id ? savedEntry : e
          );
        });
        return updated;
      });
    } else if (addingSpecialHoursFor === 'all') {
      // Add to all occasions that inherit (or all if specialHoursMode is 'all')
      setSpecialHours(prev => {
        const updated = { ...prev };
        availableOccasions.forEach(occ => {
          updated[occ] = [...(updated[occ] || []), savedEntry];
        });
        return updated;
      });
    } else if (addingSpecialHoursFor) {
      // Add to specific occasion
      setSpecialHours(prev => ({
        ...prev,
        [addingSpecialHoursFor]: [...(prev[addingSpecialHoursFor] || []), savedEntry]
      }));
    }

    setSpecialHoursDialogOpen(false);
    setAddingSpecialHoursFor(null);
    setEditingSpecialHoursEntry(null);
  };

  const handleEditSpecialHours = (entry: SpecialHoursEntryV2) => {
    setEditingSpecialHoursEntry(entry);
    setAddingSpecialHoursFor(null);
    setSpecialHoursDialogOpen(true);
  };

  // Show delete confirmation dialog
  const handleDeleteSpecialHoursClick = (occasion: OccasionType, entry: SpecialHoursEntryV2) => {
    setDeleteConfirmDialog({ open: true, occasion, entry });
  };

  // Actually perform the delete after confirmation
  const handleConfirmDeleteSpecialHours = () => {
    const { occasion, entry } = deleteConfirmDialog;
    if (occasion && entry?.id) {
      setSpecialHours(prev => ({
        ...prev,
        [occasion]: (prev[occasion] || []).filter(e => e.id !== entry.id)
      }));
    }
    setDeleteConfirmDialog({ open: false, occasion: null, entry: null });
  };

  // Get all special hours for display (primary + inherited)
  const getAllSpecialHours = (): SpecialHoursEntryV2[] => {
    const primarySpecialHours = specialHours[primaryOccasion] || [];
    return primarySpecialHours;
  };

  // Other occasions (not primary)
  const otherOccasions = availableOccasions.filter(occ => occ !== primaryOccasion);

  return (
    <Box>
      {/* Primary Occasion Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.02)
        }}
      >
        <Stack spacing={3}>
          {/* Primary Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <StarIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                Primary Hours
              </Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={primaryOccasion}
                  onChange={(e) => handlePrimaryChange(e.target.value as OccasionType)}
                  sx={{
                    fontWeight: 'medium',
                    '& .MuiSelect-select': { py: 0.75 }
                  }}
                >
                  {availableOccasions.map(occ => (
                    <MenuItem key={occ} value={occ}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{OCCASION_ICONS[occ]}</span>
                        <span>{OCCASION_LABELS[occ]}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <HelpTooltip title={OCCASION_HELP[primaryOccasion]} />
            </Stack>

            {/* Template Selector */}
            {showTemplates && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Apply Template</InputLabel>
                <Select
                  label="Apply Template"
                  onChange={(e) => applyTemplate(e.target.value as string)}
                  defaultValue=""
                >
                  <MenuItem value="" disabled>
                    <em>Choose a template</em>
                  </MenuItem>
                  {TEMPLATES.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Box>
                        <Typography variant="body2">{t.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          <Alert severity="info" sx={{ py: 0.5 }}>
            Set {OCCASION_LABELS[primaryOccasion]} hours here. Other occasions inherit these hours unless you switch them to Override.
          </Alert>

          {/* Primary Schedule Editor */}
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight="medium">
              Weekly Schedule
            </Typography>

            {DAYS_ORDER.map(day => (
              <DayHoursRow
                key={day}
                day={day}
                enabled={primarySchedule[day]?.enabled ?? false}
                timeSlots={primarySchedule[day]?.timeSlots ?? []}
                onToggleDay={(enabled) => handlePrimaryToggleDay(day, enabled)}
                onUpdateTimeSlot={(index, field, value) => handlePrimaryUpdateTimeSlot(day, index, field, value)}
                onAddTimeSlot={() => handlePrimaryAddTimeSlot(day)}
                onRemoveTimeSlot={(index) => handlePrimaryRemoveTimeSlot(day, index)}
                isToday={day === today}
                use24Hour={use24HourFormat}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      {/* Other Occasions Section */}
      {otherOccasions.length > 0 && (
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Other Occasions
          </Typography>

          <Stack spacing={2}>
            {otherOccasions.map(occasion => {
              const mode = occasionModes[occasion];
              const isExpanded = expandedOccasions.has(occasion);
              const schedule = mode === 'override' && overrideSchedules[occasion]
                ? overrideSchedules[occasion]
                : primarySchedule;

              return (
                <Paper
                  key={occasion}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: mode === 'override' ? 'warning.main' : 'divider',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  {/* Occasion Header */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: mode === 'override'
                        ? alpha(theme.palette.warning.main, 0.05)
                        : 'background.paper',
                      cursor: 'pointer'
                    }}
                    onClick={() => mode === 'override' && toggleExpanded(occasion)}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="h6" sx={{ minWidth: 120 }}>
                          {OCCASION_ICONS[occasion]} {OCCASION_LABELS[occasion]}
                        </Typography>
                        <Box onClick={(e) => e.stopPropagation()}>
                          <HelpTooltip title={OCCASION_HELP[occasion]} />
                        </Box>

                        {/* Mode Toggle */}
                        <ToggleButtonGroup
                          value={mode}
                          exclusive
                          onChange={(_, newMode) => newMode && handleModeChange(occasion, newMode)}
                          size="small"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ToggleButton
                            value="inherit"
                            sx={{
                              px: 2,
                              '&.Mui-selected': {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.2)
                                }
                              }
                            }}
                          >
                            <LinkIcon sx={{ mr: 0.5, fontSize: 18 }} />
                            Inherit
                          </ToggleButton>
                          <ToggleButton
                            value="override"
                            sx={{
                              px: 2,
                              '&.Mui-selected': {
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                color: 'warning.dark',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.warning.main, 0.2)
                                }
                              }
                            }}
                          >
                            <EditIcon sx={{ mr: 0.5, fontSize: 18 }} />
                            Override
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        {mode === 'inherit' ? (
                          <Chip
                            icon={<LinkIcon />}
                            label={`Using ${OCCASION_LABELS[primaryOccasion]} hours`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {summarizeHours(schedule, use24HourFormat)}
                            </Typography>
                            <IconButton size="small">
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Inherited Hours Summary (when not expanded) */}
                  {mode === 'inherit' && (
                    <Box sx={{ px: 2, pb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {summarizeHours(primarySchedule, use24HourFormat)}
                      </Typography>
                    </Box>
                  )}

                  {/* Override Schedule Editor */}
                  <Collapse in={mode === 'override' && isExpanded}>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        {DAYS_ORDER.map(day => (
                          <DayHoursRow
                            key={day}
                            day={day}
                            enabled={schedule[day]?.enabled ?? false}
                            timeSlots={schedule[day]?.timeSlots ?? []}
                            onToggleDay={(enabled) => handleOverrideToggleDay(occasion, day, enabled)}
                            onUpdateTimeSlot={(index, field, value) => handleOverrideUpdateTimeSlot(occasion, day, index, field, value)}
                            onAddTimeSlot={() => handleOverrideAddTimeSlot(occasion, day)}
                            onRemoveTimeSlot={(index) => handleOverrideRemoveTimeSlot(occasion, day, index)}
                            isToday={day === today}
                            use24Hour={use24HourFormat}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Special Hours Section */}
      {showSpecialHours && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mt: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <EventIcon sx={{ color: 'info.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Special Hours
                </Typography>
                <Chip
                  label={`${getAllSpecialHours().length} entries`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddSpecialHours('all')}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                Add Special Hours
              </Button>
            </Stack>

            <Alert severity="info" sx={{ py: 0.5 }}>
              Special hours (holidays, closures, etc.) will apply to all occasions. They override regular hours for the specified dates.
            </Alert>

            {getAllSpecialHours().length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.grey[500], 0.05),
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              >
                <EventIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  No special hours configured
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add holiday hours, temporary closures, or modified hours for specific dates
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {getAllSpecialHours().map((entry, index) => (
                  <ListItem
                    key={entry.id || index}
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: index % 2 === 0 ? 'transparent' : alpha(theme.palette.grey[500], 0.03),
                      borderRadius: 1,
                      mb: 0.5
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                      {entry.isClosed ? (
                        <BlockIcon sx={{ color: 'error.main' }} />
                      ) : (
                        <EventIcon sx={{ color: 'info.main' }} />
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {format(parseISO(entry.startDate), 'd MMM yyyy')}
                          {entry.endDate !== entry.startDate && (
                            <> — {format(parseISO(entry.endDate), 'd MMM yyyy')}</>
                          )}
                        </Typography>
                        <Chip
                          label={entry.isClosed ? 'Closed' : `${entry.openTime} - ${entry.closeTime}`}
                          size="small"
                          color={entry.isClosed ? 'error' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    </Stack>
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => handleEditSpecialHours(entry)}
                        sx={{ color: 'primary.main', mr: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleDeleteSpecialHoursClick(primaryOccasion, entry)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        </Paper>
      )}

      {/* Add/Edit Special Hours Dialog */}
      <AddSpecialHoursDialog
        open={specialHoursDialogOpen}
        onClose={() => {
          setSpecialHoursDialogOpen(false);
          setAddingSpecialHoursFor(null);
          setEditingSpecialHoursEntry(null);
        }}
        onSave={handleSaveSpecialHours}
        existingEntry={editingSpecialHoursEntry ? {
          id: editingSpecialHoursEntry.id,
          startDate: parseISO(editingSpecialHoursEntry.startDate),
          endDate: parseISO(editingSpecialHoursEntry.endDate),
          isClosed: editingSpecialHoursEntry.isClosed,
          openTime: editingSpecialHoursEntry.openTime,
          closeTime: editingSpecialHoursEntry.closeTime,
          occasions: availableOccasions
        } : undefined}
        availableOccasions={availableOccasions}
        use24Hour={use24HourFormat}
      />

      {/* Delete Special Hours Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, occasion: null, entry: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Special Hours?</DialogTitle>
        <DialogContent>
          {deleteConfirmDialog.entry && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Special Hours</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteConfirmDialog.entry.startDate && format(
                  typeof deleteConfirmDialog.entry.startDate === 'string'
                    ? parseISO(deleteConfirmDialog.entry.startDate)
                    : deleteConfirmDialog.entry.startDate,
                  'd MMM yyyy'
                )}
                {deleteConfirmDialog.entry.endDate && deleteConfirmDialog.entry.startDate !== deleteConfirmDialog.entry.endDate && (
                  <> — {format(
                    typeof deleteConfirmDialog.entry.endDate === 'string'
                      ? parseISO(deleteConfirmDialog.entry.endDate)
                      : deleteConfirmDialog.entry.endDate,
                    'd MMM yyyy'
                  )}</>
                )}
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                This action cannot be undone.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, occasion: null, entry: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteSpecialHours}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UnifiedHoursEditor;
